import React, { useState, useEffect, useMemo } from 'react';
import {
  SalaryCalculationMode,
  PayFrequency,
  FilingStatus,
  EstimationLevel,
  SalaryCalculationInput,
  SalaryCalculationResult,
  SavedSalaryScenario,
} from '../../types';
import {
  SALARY_COUNTRY_PACKS,
  GOLDEN_TEST_CASES,
  SALARY_TELEMETRY_DATA,
  RECENT_TAX_DIFFS,
  RECENT_TAX_INCIDENTS,
  DELIVERABLES_P6,
} from '../../data/salaryTaxPacksData';
import { salaryEngine } from '../../services/salaryEngine';
import {
  Calculator,
  Receipt,
  Scale,
  ArrowRightLeft,
  Briefcase,
  Layers,
  ShieldCheck,
  FileText,
  Info,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  Bookmark,
  Share2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Building,
  TrendingDown,
  PieChart,
  HelpCircle,
  Check,
  ExternalLink,
  BookOpen,
} from 'lucide-react';

interface SalaryIntelligenceHubProps {
  onNavigateHome?: () => void;
  onNavigateFx?: () => void;
  onNavigateTransfers?: () => void;
  onNavigateGovernance?: () => void;
}

export const SalaryIntelligenceHub: React.FC<SalaryIntelligenceHubProps> = ({
  onNavigateHome,
  onNavigateFx,
  onNavigateTransfers,
  onNavigateGovernance,
}) => {
  // Mode selection
  const [activeTab, setActiveTab] = useState<
    | 'salary-calculator'
    | 'paycheck-calculator'
    | 'income-tax-calculator'
    | 'net-to-gross'
    | 'employer-cost'
    | 'salary-comparison'
    | 'command-center'
    | 'architecture'
  >('salary-calculator');

  // Input states
  const [selectedCountry, setSelectedCountry] = useState<string>('US');
  const [selectedSubdivision, setSelectedSubdivision] = useState<string>('CA');
  const [selectedLocal, setSelectedLocal] = useState<string>('');
  const [grossAmount, setGrossAmount] = useState<number>(85000);
  const [targetNetAmount, setTargetNetAmount] = useState<number>(5000); // for Net-to-Gross
  const [payFrequency, setPayFrequency] = useState<PayFrequency>('annual');
  const [targetNetFrequency, setTargetNetFrequency] = useState<PayFrequency>('monthly');
  const [filingStatus, setFilingStatus] = useState<FilingStatus>('SINGLE');
  const [estimationLevel, setEstimationLevel] = useState<EstimationLevel>('DETAILED_ESTIMATE');

  // Advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [pensionEmployeePercent, setPensionEmployeePercent] = useState<number>(0);
  const [pensionEmployerPercent, setPensionEmployerPercent] = useState<number>(0);
  const [preTaxHealthInsurance, setPreTaxHealthInsurance] = useState<number>(0);
  const [hoursPerWeek, setHoursPerWeek] = useState<number>(40);

  // Results & UI State
  const [activeDisplayPeriod, setActiveDisplayPeriod] = useState<'monthly' | 'annual' | 'period' | 'hourly'>('monthly');
  const [savedScenarios, setSavedScenarios] = useState<SavedSalaryScenario[]>([]);
  const [scenarioNameInput, setScenarioNameInput] = useState<string>('');
  const [showSaveSuccess, setShowSaveSuccess] = useState<boolean>(false);
  const [expandedTrace, setExpandedTrace] = useState<boolean>(false);

  // Comparison State
  const [compareCountryB, setCompareCountryB] = useState<string>('US');
  const [compareSubdivisionB, setCompareSubdivisionB] = useState<string>('TX');
  const [compareGrossB, setCompareGrossB] = useState<number>(85000);

  // Load saved scenarios from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('yamoopay_saved_salary_scenarios');
      if (stored) {
        setSavedScenarios(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
  }, []);

  // Update country packs and defaults
  const activeCountryPack = SALARY_COUNTRY_PACKS[selectedCountry] || SALARY_COUNTRY_PACKS['US'];

  // Keep subdivision valid when country changes
  useEffect(() => {
    if (activeCountryPack.subdivisions && activeCountryPack.subdivisions.length > 0) {
      setSelectedSubdivision(activeCountryPack.subdivisions[0].code);
    } else {
      setSelectedSubdivision('');
    }
    setSelectedLocal('');
  }, [selectedCountry, activeCountryPack]);

  // Compute primary forward calculation
  const calculationResult: SalaryCalculationResult = useMemo(() => {
    const input: SalaryCalculationInput = {
      mode: activeTab === 'net-to-gross' ? 'net-to-gross' : (activeTab as SalaryCalculationMode),
      countryCode: selectedCountry,
      subdivisionCode: selectedSubdivision || undefined,
      localJurisdictionCode: selectedLocal || undefined,
      grossAmount,
      payFrequency,
      hoursPerWeek,
      taxPeriodId: activeCountryPack.taxPeriodId,
      filingStatus,
      pensionEmployeePercent,
      pensionEmployerPercent,
      preTaxDeductions: preTaxHealthInsurance > 0 ? [{ id: 'health', name: 'Health Insurance', amount: preTaxHealthInsurance }] : [],
      estimationLevel,
    };
    return salaryEngine.calculateSalary(input);
  }, [
    activeTab,
    selectedCountry,
    selectedSubdivision,
    selectedLocal,
    grossAmount,
    payFrequency,
    hoursPerWeek,
    activeCountryPack,
    filingStatus,
    pensionEmployeePercent,
    pensionEmployerPercent,
    preTaxHealthInsurance,
    estimationLevel,
  ]);

  // Compute Net-to-Gross reverse calculation
  const netToGrossResult = useMemo(() => {
    return salaryEngine.solveNetToGross(targetNetAmount, targetNetFrequency, {
      countryCode: selectedCountry,
      subdivisionCode: selectedSubdivision || undefined,
      localJurisdictionCode: selectedLocal || undefined,
      taxPeriodId: activeCountryPack.taxPeriodId,
      filingStatus,
      pensionEmployeePercent,
      pensionEmployerPercent,
      preTaxDeductions: preTaxHealthInsurance > 0 ? [{ id: 'health', name: 'Health Insurance', amount: preTaxHealthInsurance }] : [],
      estimationLevel,
    });
  }, [
    targetNetAmount,
    targetNetFrequency,
    selectedCountry,
    selectedSubdivision,
    selectedLocal,
    activeCountryPack,
    filingStatus,
    pensionEmployeePercent,
    pensionEmployerPercent,
    preTaxHealthInsurance,
    estimationLevel,
  ]);

  // Compute Scenario B for comparison
  const comparisonResultB: SalaryCalculationResult = useMemo(() => {
    const packB = SALARY_COUNTRY_PACKS[compareCountryB] || SALARY_COUNTRY_PACKS['US'];
    const inputB: SalaryCalculationInput = {
      mode: 'salary-comparison',
      countryCode: compareCountryB,
      subdivisionCode: compareSubdivisionB || undefined,
      grossAmount: compareGrossB,
      payFrequency: 'annual',
      taxPeriodId: packB.taxPeriodId,
      filingStatus,
      estimationLevel,
    };
    return salaryEngine.calculateSalary(inputB);
  }, [compareCountryB, compareSubdivisionB, compareGrossB, filingStatus, estimationLevel]);

  // Golden tests QA
  const goldenTestReport = useMemo(() => {
    return salaryEngine.runGoldenTests();
  }, []);

  // Save scenario handler
  const handleSaveScenario = () => {
    const name = scenarioNameInput.trim() || `${activeCountryPack.countryName} ${calculationResult.jurisdictionName} (${calculationResult.annualGross.toLocaleString()} ${calculationResult.currency})`;
    const newScenario: SavedSalaryScenario = {
      id: `SCENARIO-${Date.now()}`,
      name,
      createdAt: new Date().toISOString(),
      input: calculationResult.input,
      result: calculationResult,
    };
    const updated = [newScenario, ...savedScenarios.slice(0, 9)];
    setSavedScenarios(updated);
    try {
      localStorage.setItem('yamoopay_saved_salary_scenarios', JSON.stringify(updated));
    } catch {
      // ignore
    }
    setScenarioNameInput('');
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const handleDeleteScenario = (id: string) => {
    const updated = savedScenarios.filter((s) => s.id !== id);
    setSavedScenarios(updated);
    try {
      localStorage.setItem('yamoopay_saved_salary_scenarios', JSON.stringify(updated));
    } catch {
      // ignore
    }
  };

  // Helper for active take home amount based on display toggle
  const getDisplayTakeHome = (res: SalaryCalculationResult) => {
    switch (activeDisplayPeriod) {
      case 'annual':
        return { amount: res.annualNetPay, label: '/ year', grossLabel: res.annualGross };
      case 'period':
        return { amount: res.periodNetPay, label: `/ ${res.input.payFrequency}`, grossLabel: res.periodGross };
      case 'hourly':
        return { amount: res.hourlyEquivalentNet, label: '/ hour', grossLabel: res.annualGross / 2080 };
      case 'monthly':
      default:
        return { amount: res.monthlyNetPay, label: '/ month', grossLabel: res.monthlyGross };
    }
  };

  const displayTakeHome = getDisplayTakeHome(calculationResult);

  return (
    <div className="w-full bg-[#FAFAF8] min-h-screen text-[#1A1A1A]">
      {/* 1. Header Banner */}
      <div className="bg-white border-b border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#E8F5E9] text-[#0E7044] border border-[#C8E6C9]">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  PROMPT 06 — Global Salary & Tax Intelligence Engine
                </span>
                <span className="text-xs text-[#767676] flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {activeCountryPack.taxYearLabel} • Ruleset {activeCountryPack.rulesetVersion}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1A1A1A] tracking-tight">
                Global Salary, Paycheck & Income Tax Engine
              </h1>
              <p className="text-sm text-[#555555] max-w-3xl mt-1">
                Deterministic gross-to-net calculations, reverse net-to-gross solver, total employer cost of employment, and explainable 2026 progressive tax breakdown across 50+ jurisdictions.
              </p>
            </div>

            {/* Quick action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setActiveTab('command-center')}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#F5F5F3] text-[#1A1A1A] hover:bg-[#EBEBE8] border border-[#E5E5E0] transition-colors"
              >
                <Layers className="w-3.5 h-3.5 text-[#0E7044]" />
                Tax Command Center
              </button>
              <button
                onClick={() => setActiveTab('architecture')}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#F5F5F3] text-[#1A1A1A] hover:bg-[#EBEBE8] border border-[#E5E5E0] transition-colors"
              >
                <FileText className="w-3.5 h-3.5 text-[#1A1A1A]" />
                ADR Deliverables (P6)
              </button>
            </div>
          </div>

          {/* 2. Navigation Mode Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pt-5 border-t border-[#F0F0EC] mt-5 pb-1">
            {[
              { id: 'salary-calculator', label: '1. Salary Calculator', icon: Calculator },
              { id: 'paycheck-calculator', label: '2. Paycheck Calculator', icon: Receipt },
              { id: 'income-tax-calculator', label: '3. Income Tax & Brackets', icon: Scale },
              { id: 'net-to-gross', label: '4. Net-to-Gross Solver', icon: ArrowRightLeft },
              { id: 'employer-cost', label: '5. Employer Cost Calculator', icon: Briefcase },
              { id: 'salary-comparison', label: '6. Compare Scenarios', icon: Sparkles },
              { id: 'command-center', label: 'Tax Command Center', icon: Layers },
              { id: 'architecture', label: 'Architecture & Governance', icon: FileText },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-lg whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-[#0E7044] text-white shadow-sm'
                      : 'text-[#555555] hover:text-[#1A1A1A] hover:bg-[#F0F0EC]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. Main Workspace Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* VIEW 1 TO 5: THE PRIMARY CALCULATOR & SOLVER WORKSPACE */}
        {activeTab !== 'salary-comparison' && activeTab !== 'command-center' && activeTab !== 'architecture' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column: Interactive Inputs (5 Cols) */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
                <div className="flex items-center justify-between pb-4 border-b border-[#F0F0EC] mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#E8F5E9] flex items-center justify-center text-[#0E7044]">
                      <Calculator className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-[#1A1A1A]">
                        {activeTab === 'net-to-gross'
                          ? 'Desired Take-Home Target'
                          : activeTab === 'employer-cost'
                          ? 'Base Employee Compensation'
                          : 'Salary & Jurisdiction Inputs'}
                      </h2>
                      <p className="text-xs text-[#767676]">Published 2026 Statutory Rulesets</p>
                    </div>
                  </div>

                  {/* Estimation Level Badge */}
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#F5F5F3] text-[#555555] border border-[#E5E5E0]">
                    {estimationLevel === 'DETAILED_ESTIMATE' ? 'Detailed Estimate' : 'Quick Estimate'}
                  </span>
                </div>

                <div className="space-y-4">
                  {/* Country Selector */}
                  <div>
                    <label className="block text-xs font-bold text-[#1A1A1A] mb-1">Country / Tax System</label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      className="w-full bg-[#FAFAF8] border border-[#E5E5E0] rounded-lg px-3 py-2.5 text-sm font-medium text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#0E7044]"
                    >
                      {Object.values(SALARY_COUNTRY_PACKS).map((pack) => (
                        <option key={pack.countryCode} value={pack.countryCode}>
                          {pack.countryName} ({pack.defaultCurrency}) — {pack.taxPeriodId}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* State / Province / Canton Selector (if available) */}
                  {activeCountryPack.subdivisions && activeCountryPack.subdivisions.length > 0 && (
                    <div>
                      <label className="block text-xs font-bold text-[#1A1A1A] mb-1">
                        {selectedCountry === 'US'
                          ? 'State / Territory'
                          : selectedCountry === 'CA'
                          ? 'Province / Territory'
                          : selectedCountry === 'GB'
                          ? 'Region / Tax Code System'
                          : 'Regional Jurisdiction'}
                      </label>
                      <select
                        value={selectedSubdivision}
                        onChange={(e) => setSelectedSubdivision(e.target.value)}
                        className="w-full bg-[#FAFAF8] border border-[#E5E5E0] rounded-lg px-3 py-2.5 text-sm font-medium text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#0E7044]"
                      >
                        {activeCountryPack.subdivisions.map((sub) => (
                          <option key={sub.code} value={sub.code}>
                            {sub.name} {sub.hasStateTax ? '' : '(0% State Wage Tax)'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Local City Tax (e.g. NYC in NY state) */}
                  {selectedCountry === 'US' && selectedSubdivision === 'NY' && (
                    <div>
                      <label className="block text-xs font-bold text-[#1A1A1A] mb-1">Local / Municipality Tax</label>
                      <select
                        value={selectedLocal}
                        onChange={(e) => setSelectedLocal(e.target.value)}
                        className="w-full bg-[#FAFAF8] border border-[#E5E5E0] rounded-lg px-3 py-2 text-xs font-medium text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#0E7044]"
                      >
                        <option value="">None (Standard NY State Only)</option>
                        <option value="NYC">New York City Resident Tax (+3.876%)</option>
                        <option value="YONKERS">Yonkers Resident Tax (+1.675%)</option>
                      </select>
                    </div>
                  )}

                  {/* PRIMARY AMOUNT INPUT */}
                  {activeTab === 'net-to-gross' ? (
                    <div>
                      <label className="block text-xs font-bold text-[#1A1A1A] mb-1">
                        Target Net Take-Home Pay ({activeCountryPack.defaultCurrency})
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[#767676]">
                          {activeCountryPack.defaultCurrency}
                        </span>
                        <input
                          type="number"
                          value={targetNetAmount}
                          onChange={(e) => setTargetNetAmount(Math.max(0, Number(e.target.value)))}
                          className="w-full bg-[#FAFAF8] border border-[#E5E5E0] rounded-lg pl-14 pr-4 py-2.5 text-base font-bold text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#0E7044]"
                          placeholder="e.g. 5000"
                        />
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        {(['monthly', 'annual', 'biweekly'] as PayFrequency[]).map((f) => (
                          <button
                            key={f}
                            onClick={() => setTargetNetFrequency(f)}
                            className={`flex-1 py-1 text-xs font-bold rounded border transition-colors ${
                              targetNetFrequency === f
                                ? 'bg-[#0E7044] text-white border-[#0E7044]'
                                : 'bg-[#FAFAF8] text-[#555555] border-[#E5E5E0] hover:bg-[#F0F0EC]'
                            }`}
                          >
                            Per {f}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-bold text-[#1A1A1A] mb-1">
                        {activeTab === 'employer-cost' ? 'Gross Salary Offered' : 'Gross Salary / Compensation'} ({activeCountryPack.defaultCurrency})
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[#767676]">
                          {activeCountryPack.defaultCurrency}
                        </span>
                        <input
                          type="number"
                          value={grossAmount}
                          onChange={(e) => setGrossAmount(Math.max(0, Number(e.target.value)))}
                          className="w-full bg-[#FAFAF8] border border-[#E5E5E0] rounded-lg pl-14 pr-4 py-2.5 text-base font-bold text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#0E7044]"
                          placeholder="e.g. 85000"
                        />
                      </div>

                      {/* Frequency Switcher Pills */}
                      <div className="grid grid-cols-4 gap-1 mt-2">
                        {(['annual', 'monthly', 'biweekly', 'hourly'] as PayFrequency[]).map((freq) => (
                          <button
                            key={freq}
                            onClick={() => setPayFrequency(freq)}
                            className={`py-1 text-xs font-bold rounded border transition-colors ${
                              payFrequency === freq
                                ? 'bg-[#0E7044] text-white border-[#0E7044]'
                                : 'bg-[#FAFAF8] text-[#555555] border-[#E5E5E0] hover:bg-[#F0F0EC]'
                            }`}
                          >
                            {freq === 'annual' ? 'Per Year' : freq === 'monthly' ? 'Monthly' : freq === 'biweekly' ? 'Bi-Weekly' : 'Hourly'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Filing Status (if applicable to country) */}
                  {activeCountryPack.supportedFilingStatuses.length > 1 && (
                    <div>
                      <label className="block text-xs font-bold text-[#1A1A1A] mb-1">Filing Status / Household Category</label>
                      <select
                        value={filingStatus}
                        onChange={(e) => setFilingStatus(e.target.value as FilingStatus)}
                        className="w-full bg-[#FAFAF8] border border-[#E5E5E0] rounded-lg px-3 py-2 text-xs font-medium text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#0E7044]"
                      >
                        <option value="SINGLE">Single Filer</option>
                        <option value="MARRIED_JOINT">Married Filing Jointly (Double Standard Deduction)</option>
                        <option value="HEAD_OF_HOUSEHOLD">Head of Household</option>
                        <option value="MARRIED_SEPARATE">Married Filing Separately</option>
                      </select>
                    </div>
                  )}

                  {/* Progressive Disclosure: Advanced Settings */}
                  <div className="pt-2 border-t border-[#F0F0EC]">
                    <button
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="w-full flex items-center justify-between text-xs font-bold text-[#0E7044] py-1 hover:underline"
                    >
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" />
                        {showAdvanced ? 'Hide Advanced Payroll Options' : 'Show Advanced Payroll & Deductions'}
                      </span>
                      {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {showAdvanced && (
                      <div className="space-y-3 pt-3 mt-2 bg-[#FAFAF8] p-3 rounded-lg border border-[#E5E5E0] text-xs">
                        <div>
                          <label className="block font-bold text-[#1A1A1A] mb-1">
                            Employee Voluntary Pension / 401(k) (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            value={pensionEmployeePercent}
                            onChange={(e) => setPensionEmployeePercent(Number(e.target.value))}
                            className="w-full bg-white border border-[#E5E5E0] rounded px-2.5 py-1.5 font-medium text-[#1A1A1A]"
                            placeholder="e.g. 5%"
                          />
                        </div>

                        <div>
                          <label className="block font-bold text-[#1A1A1A] mb-1">
                            Employer Pension Matching (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            value={pensionEmployerPercent}
                            onChange={(e) => setPensionEmployerPercent(Number(e.target.value))}
                            className="w-full bg-white border border-[#E5E5E0] rounded px-2.5 py-1.5 font-medium text-[#1A1A1A]"
                            placeholder="e.g. 3%"
                          />
                        </div>

                        <div>
                          <label className="block font-bold text-[#1A1A1A] mb-1">
                            Pre-Tax Annual Health Insurance Premium ({activeCountryPack.defaultCurrency})
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={preTaxHealthInsurance}
                            onChange={(e) => setPreTaxHealthInsurance(Number(e.target.value))}
                            className="w-full bg-white border border-[#E5E5E0] rounded px-2.5 py-1.5 font-medium text-[#1A1A1A]"
                            placeholder="e.g. 2400"
                          />
                        </div>

                        <div>
                          <label className="block font-bold text-[#1A1A1A] mb-1">
                            Standard Weekly Working Hours
                          </label>
                          <input
                            type="number"
                            min="10"
                            max="80"
                            value={hoursPerWeek}
                            onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                            className="w-full bg-white border border-[#E5E5E0] rounded px-2.5 py-1.5 font-medium text-[#1A1A1A]"
                            placeholder="40"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Save Scenario Card */}
              <div className="bg-white rounded-xl border border-[#E5E5E0] p-4 shadow-xs">
                <h3 className="text-xs font-bold text-[#1A1A1A] mb-2 flex items-center gap-1.5">
                  <Bookmark className="w-3.5 h-3.5 text-[#0E7044]" />
                  Save Scenario to Local Session
                </h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={scenarioNameInput}
                    onChange={(e) => setScenarioNameInput(e.target.value)}
                    placeholder="Scenario label (e.g. Tech Offer SF)"
                    className="flex-1 bg-[#FAFAF8] border border-[#E5E5E0] rounded-lg px-3 py-1.5 text-xs text-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#0E7044]"
                  />
                  <button
                    onClick={handleSaveScenario}
                    className="px-3 py-1.5 bg-[#0E7044] text-white rounded-lg text-xs font-bold hover:bg-[#0B5A36] transition-colors"
                  >
                    Save
                  </button>
                </div>
                {showSaveSuccess && (
                  <p className="text-[11px] font-bold text-[#0E7044] mt-1.5 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Scenario saved successfully.
                  </p>
                )}

                {savedScenarios.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#F0F0EC] space-y-1.5">
                    <p className="text-[11px] font-bold text-[#767676]">Saved Calculations ({savedScenarios.length})</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {savedScenarios.map((s) => (
                        <div
                          key={s.id}
                          className="flex items-center justify-between p-1.5 bg-[#FAFAF8] rounded border border-[#E5E5E0] text-[11px]"
                        >
                          <span className="font-medium truncate max-w-[200px]">{s.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-[#0E7044]">
                              {s.result.monthlyNetPay.toLocaleString()} {s.result.currency}/mo
                            </span>
                            <button
                              onClick={() => handleDeleteScenario(s.id)}
                              className="text-red-500 hover:text-red-700 text-[10px]"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Data Verification & Source Box */}
              <div className="bg-[#FAFAF8] rounded-xl border border-[#E5E5E0] p-4 text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#1A1A1A] flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#0E7044]" />
                    Official Revenue Authority Source
                  </span>
                  <span className="text-[10px] bg-[#E8F5E9] text-[#0E7044] font-bold px-1.5 py-0.5 rounded">
                    Verified {activeCountryPack.verifiedAt}
                  </span>
                </div>
                <div className="space-y-1">
                  {activeCountryPack.officialSources.map((src, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] text-[#555555]">
                      <span className="truncate">{src.name}</span>
                      <a
                        href={src.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#0E7044] hover:underline flex items-center gap-0.5 ml-2"
                      >
                        Source <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[#767676] italic pt-1 border-t border-[#E5E5E0]">
                  {activeCountryPack.assumptions[0]}
                </p>
              </div>
            </div>

            {/* Right Column: High-Precision Result Hero, Breakdown & Traces (7 Cols) */}
            <div className="lg:col-span-7 space-y-6">
              {/* If in Net-to-Gross Mode, Show the Solver Banner */}
              {activeTab === 'net-to-gross' ? (
                <div className="bg-white rounded-xl border-2 border-[#0E7044] p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#0E7044]">
                      YamooPay Reverse Salary Solver
                    </span>
                    <span className="text-xs text-[#767676]">
                      Converged in {netToGrossResult.iterationsCount} iterations (Tolerance: &lt; 5¢)
                    </span>
                  </div>
                  <h3 className="text-xs font-bold text-[#555555]">Required Gross Salary to take home</h3>
                  <div className="flex items-baseline gap-2 mt-1 mb-4">
                    <span className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A]">
                      {netToGrossResult.requiredGrossAnnual.toLocaleString()} {activeCountryPack.defaultCurrency}
                    </span>
                    <span className="text-base font-bold text-[#555555]">/ year</span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3.5 bg-[#FAFAF8] rounded-lg border border-[#E5E5E0] text-xs">
                    <div>
                      <span className="text-[#767676] block">Required Monthly Gross</span>
                      <span className="font-bold text-[#1A1A1A] text-sm">
                        {netToGrossResult.requiredGrossMonthly.toLocaleString()} {activeCountryPack.defaultCurrency}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#767676] block">Target Take-Home</span>
                      <span className="font-bold text-[#0E7044] text-sm">
                        {targetNetAmount.toLocaleString()} {activeCountryPack.defaultCurrency} / {targetNetFrequency}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#767676] block">Estimated Total Tax & Social</span>
                      <span className="font-bold text-[#1A1A1A] text-sm">
                        {(netToGrossResult.requiredGrossAnnual - netToGrossResult.forwardCalculation.annualNetPay).toLocaleString()} {activeCountryPack.defaultCurrency}
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* PRIMARY RESULT HERO */}
              <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
                {/* Timeframe Toggle Buttons */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#0E7044]">
                      {activeTab === 'employer-cost' ? 'Total Employer Cost' : 'Estimated Net Take-Home Pay'}
                    </span>
                    <h3 className="text-xs text-[#767676]">
                      {calculationResult.jurisdictionName} • {activeCountryPack.taxYearLabel}
                    </h3>
                  </div>

                  <div className="flex items-center gap-1 bg-[#FAFAF8] p-1 rounded-lg border border-[#E5E5E0]">
                    {(['monthly', 'annual', 'period', 'hourly'] as const).map((period) => (
                      <button
                        key={period}
                        onClick={() => setActiveDisplayPeriod(period)}
                        className={`px-2.5 py-1 text-xs font-bold rounded transition-colors ${
                          activeDisplayPeriod === period
                            ? 'bg-white text-[#1A1A1A] shadow-xs'
                            : 'text-[#555555] hover:text-[#1A1A1A]'
                        }`}
                      >
                        {period === 'monthly' ? 'Monthly' : period === 'annual' ? 'Annual' : period === 'period' ? 'Paycheck' : 'Hourly'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Big Number Display */}
                <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-6 pb-6 border-b border-[#F0F0EC]">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] tracking-tight">
                        {activeTab === 'employer-cost'
                          ? (activeDisplayPeriod === 'monthly'
                              ? calculationResult.totalEmployerCost / 12
                              : activeDisplayPeriod === 'hourly'
                              ? calculationResult.totalEmployerCost / 2080
                              : calculationResult.totalEmployerCost
                            ).toLocaleString(undefined, { maximumFractionDigits: 0 })
                          : displayTakeHome.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                      <span className="text-xl font-bold text-[#0E7044]">
                        {calculationResult.currency}
                      </span>
                      <span className="text-sm font-semibold text-[#555555]">
                        {displayTakeHome.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#767676] mt-1">
                      Gross: {displayTakeHome.grossLabel.toLocaleString(undefined, { maximumFractionDigits: 0 })} {calculationResult.currency} {displayTakeHome.label}
                    </p>
                  </div>

                  {/* Summary Metric Badges */}
                  <div className="flex sm:flex-col items-end gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-[#F5F5F3] text-xs font-bold text-[#1A1A1A]">
                      Effective Tax: {(calculationResult.effectiveTaxRate * 100).toFixed(1)}%
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-[#E8F5E9] text-xs font-bold text-[#0E7044]">
                      Total Deductions: {(calculationResult.totalDeductionRate * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>

                {/* Stacked Proportional Bar Visualizer */}
                <div className="mb-6">
                  <div className="flex justify-between text-xs font-bold text-[#767676] mb-1.5">
                    <span>Income Decomposition</span>
                    <span>100% Gross Salary</span>
                  </div>
                  <div className="w-full h-4 rounded-full bg-[#E5E5E0] overflow-hidden flex">
                    {/* Net Pay (Green) */}
                    <div
                      style={{ width: `${(calculationResult.annualNetPay / calculationResult.annualGross) * 100}%` }}
                      className="bg-[#0E7044] h-full"
                      title={`Net Take-Home: ${((calculationResult.annualNetPay / calculationResult.annualGross) * 100).toFixed(1)}%`}
                    />
                    {/* Income Tax (Slate) */}
                    <div
                      style={{ width: `${(calculationResult.totalIncomeTax / calculationResult.annualGross) * 100}%` }}
                      className="bg-[#475569] h-full"
                      title={`Income Taxes: ${((calculationResult.totalIncomeTax / calculationResult.annualGross) * 100).toFixed(1)}%`}
                    />
                    {/* Social Security & Contributions (Amber) */}
                    <div
                      style={{ width: `${(calculationResult.totalEmployeeContributions / calculationResult.annualGross) * 100}%` }}
                      className="bg-[#D97706] h-full"
                      title={`Social Contributions: ${((calculationResult.totalEmployeeContributions / calculationResult.annualGross) * 100).toFixed(1)}%`}
                    />
                  </div>

                  {/* Legend */}
                  <div className="flex items-center justify-between text-[11px] text-[#555555] mt-2 flex-wrap gap-2">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#0E7044]" />
                      Net Take-Home: {((calculationResult.annualNetPay / calculationResult.annualGross) * 100).toFixed(1)}%
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#475569]" />
                      Income Taxes: {((calculationResult.totalIncomeTax / calculationResult.annualGross) * 100).toFixed(1)}%
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#D97706]" />
                      Social Security/FICA: {((calculationResult.totalEmployeeContributions / calculationResult.annualGross) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>

                {/* DETAILED FINANCIAL TABLE BREAKDOWN */}
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#767676]">
                    Complete Deduction & Tax Breakdown (Annual & Monthly)
                  </h4>
                  <div className="border border-[#E5E5E0] rounded-lg overflow-hidden text-xs">
                    <table className="w-full text-left">
                      <thead className="bg-[#FAFAF8] text-[#555555] font-bold border-b border-[#E5E5E0]">
                        <tr>
                          <th className="py-2 px-3">Component</th>
                          <th className="py-2 px-3 text-right">Annual</th>
                          <th className="py-2 px-3 text-right">Monthly</th>
                          <th className="py-2 px-3 text-right">% of Gross</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0F0EC]">
                        <tr className="bg-white font-bold text-[#1A1A1A]">
                          <td className="py-2.5 px-3">Gross Salary</td>
                          <td className="py-2.5 px-3 text-right">{calculationResult.annualGross.toLocaleString()} {calculationResult.currency}</td>
                          <td className="py-2.5 px-3 text-right">{calculationResult.monthlyGross.toLocaleString(undefined, { maximumFractionDigits: 0 })} {calculationResult.currency}</td>
                          <td className="py-2.5 px-3 text-right">100.0%</td>
                        </tr>

                        {calculationResult.preTaxDeductionsTotal > 0 && (
                          <tr className="text-[#555555]">
                            <td className="py-2 px-3 pl-6">↳ Pre-Tax Deductions (Health / 401k)</td>
                            <td className="py-2 px-3 text-right text-emerald-700">-{calculationResult.preTaxDeductionsTotal.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right text-emerald-700">-{(calculationResult.preTaxDeductionsTotal / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="py-2 px-3 text-right text-emerald-700">{((calculationResult.preTaxDeductionsTotal / calculationResult.annualGross) * 100).toFixed(1)}%</td>
                          </tr>
                        )}

                        <tr className="text-[#555555]">
                          <td className="py-2 px-3 pl-6">↳ Federal / National Income Tax</td>
                          <td className="py-2 px-3 text-right">-{calculationResult.federalIncomeTax.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right">-{(calculationResult.federalIncomeTax / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="py-2 px-3 text-right">{((calculationResult.federalIncomeTax / calculationResult.annualGross) * 100).toFixed(1)}%</td>
                        </tr>

                        {calculationResult.stateIncomeTax > 0 && (
                          <tr className="text-[#555555]">
                            <td className="py-2 px-3 pl-6">↳ State / Provincial Tax ({selectedSubdivision})</td>
                            <td className="py-2 px-3 text-right">-{calculationResult.stateIncomeTax.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right">-{(calculationResult.stateIncomeTax / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="py-2 px-3 text-right">{((calculationResult.stateIncomeTax / calculationResult.annualGross) * 100).toFixed(1)}%</td>
                          </tr>
                        )}

                        {calculationResult.localIncomeTax > 0 && (
                          <tr className="text-[#555555]">
                            <td className="py-2 px-3 pl-6">↳ Local City Tax ({selectedLocal})</td>
                            <td className="py-2 px-3 text-right">-{calculationResult.localIncomeTax.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right">-{(calculationResult.localIncomeTax / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="py-2 px-3 text-right">{((calculationResult.localIncomeTax / calculationResult.annualGross) * 100).toFixed(1)}%</td>
                          </tr>
                        )}

                        <tr className="text-[#555555]">
                          <td className="py-2 px-3 pl-6">↳ Social Security / OASDI / Retirement</td>
                          <td className="py-2 px-3 text-right">-{calculationResult.employeeSocialSecurity.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right">-{(calculationResult.employeeSocialSecurity / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="py-2 px-3 text-right">{((calculationResult.employeeSocialSecurity / calculationResult.annualGross) * 100).toFixed(1)}%</td>
                        </tr>

                        <tr className="text-[#555555]">
                          <td className="py-2 px-3 pl-6">↳ Medicare / Health / Unemployment</td>
                          <td className="py-2 px-3 text-right">-{calculationResult.employeeMedicare.toLocaleString()}</td>
                          <td className="py-2 px-3 text-right">-{(calculationResult.employeeMedicare / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="py-2 px-3 text-right">{((calculationResult.employeeMedicare / calculationResult.annualGross) * 100).toFixed(1)}%</td>
                        </tr>

                        <tr className="bg-[#E8F5E9] font-bold text-[#0E7044]">
                          <td className="py-2.5 px-3">Net Take-Home Pay</td>
                          <td className="py-2.5 px-3 text-right">{calculationResult.annualNetPay.toLocaleString()} {calculationResult.currency}</td>
                          <td className="py-2.5 px-3 text-right">{calculationResult.monthlyNetPay.toLocaleString(undefined, { maximumFractionDigits: 0 })} {calculationResult.currency}</td>
                          <td className="py-2.5 px-3 text-right">{((calculationResult.annualNetPay / calculationResult.annualGross) * 100).toFixed(1)}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* EMPLOYER COST OF EMPLOYMENT DECOMPOSITION CARD */}
              <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#EFF6FF] flex items-center justify-center text-[#2563EB]">
                      <Briefcase className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#1A1A1A]">
                        What Your Employer Really Pays (Total Employment Cost)
                      </h3>
                      <p className="text-xs text-[#767676]">
                        Mandatory employer taxes & statutory contributions above gross salary
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 bg-[#EFF6FF] text-[#2563EB] rounded">
                    + {((calculationResult.employerCostRatio - 1) * 100).toFixed(1)}% Overhead
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] mb-4">
                  <div>
                    <span className="text-xs text-[#64748B] block">Gross Salary to Employee</span>
                    <span className="text-base font-bold text-[#1E293B]">
                      {calculationResult.annualGross.toLocaleString()} {calculationResult.currency}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-[#64748B] block">Employer Statutory Charges</span>
                    <span className="text-base font-bold text-[#2563EB]">
                      +{calculationResult.totalEmployerContributions.toLocaleString()} {calculationResult.currency}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-[#64748B] block">Total Employer Cost</span>
                    <span className="text-base font-extrabold text-[#0E7044]">
                      {calculationResult.totalEmployerCost.toLocaleString()} {calculationResult.currency}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-[#555555]">
                  Includes employer statutory match for Social Security / Pension, Medicare, Unemployment Insurance (FUTA/SUTA), and mandatory workers compensation levies.
                </p>
              </div>

              {/* PROGRESSIVE TAX BRACKETS DECOMPOSITION */}
              <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
                <h3 className="text-sm font-bold text-[#1A1A1A] mb-1 flex items-center gap-1.5">
                  <Scale className="w-4 h-4 text-[#0E7044]" />
                  Progressive Tax Tier Breakdown (Federal / National)
                </h3>
                <p className="text-xs text-[#767676] mb-4">
                  Standard Deduction Applied: {calculationResult.standardDeductionApplied.toLocaleString()} {calculationResult.currency} • Taxable Base: {calculationResult.taxableIncome.toLocaleString()} {calculationResult.currency}
                </p>

                <div className="border border-[#E5E5E0] rounded-lg overflow-hidden text-xs">
                  <table className="w-full text-left">
                    <thead className="bg-[#FAFAF8] text-[#555555] font-bold border-b border-[#E5E5E0]">
                      <tr>
                        <th className="py-2 px-3">Tax Band Range</th>
                        <th className="py-2 px-3">Rate</th>
                        <th className="py-2 px-3 text-right">Taxable in Band</th>
                        <th className="py-2 px-3 text-right">Tax Due</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F0EC]">
                      {calculationResult.federalBracketsBreakdown.map((b, i) => (
                        <tr key={i} className={b.taxableInBracket > 0 ? 'bg-white' : 'bg-[#FAFAF8] text-[#999999]'}>
                          <td className="py-2 px-3 font-medium">
                            {b.min.toLocaleString()} — {b.max !== null ? `${b.max.toLocaleString()} ${calculationResult.currency}` : 'and above'}
                          </td>
                          <td className="py-2 px-3 font-bold">{(b.rate * 100).toFixed(1)}%</td>
                          <td className="py-2 px-3 text-right font-medium">{b.taxableInBracket.toLocaleString()} {calculationResult.currency}</td>
                          <td className="py-2 px-3 text-right font-bold text-[#1A1A1A]">{b.taxAmount.toLocaleString()} {calculationResult.currency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* HUMAN-READABLE EXPLAINABILITY TRACE ACCORDION */}
              <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
                <button
                  onClick={() => setExpandedTrace(!expandedTrace)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#E8F5E9] flex items-center justify-center text-[#0E7044]">
                      <Info className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#1A1A1A]">
                        Calculation Explainability Trace (Why was this deducted?)
                      </h3>
                      <p className="text-xs text-[#767676]">
                        {calculationResult.explainabilityTrace.length} deterministic execution steps
                      </p>
                    </div>
                  </div>
                  {expandedTrace ? <ChevronUp className="w-5 h-5 text-[#767676]" /> : <ChevronDown className="w-5 h-5 text-[#767676]" />}
                </button>

                {expandedTrace && (
                  <div className="mt-4 pt-4 border-t border-[#F0F0EC] space-y-3">
                    <p className="text-xs text-[#555555] bg-[#FAFAF8] p-3 rounded-lg border border-[#E5E5E0] leading-relaxed">
                      {calculationResult.plainLanguageExplanation}
                    </p>

                    <div className="space-y-2">
                      {calculationResult.explainabilityTrace.map((step) => (
                        <div
                          key={step.stepNumber}
                          className="p-3 bg-[#FAFAF8] rounded-lg border border-[#E5E5E0] text-xs space-y-1"
                        >
                          <div className="flex items-center justify-between font-bold">
                            <span className="text-[#1A1A1A]">
                              Step {step.stepNumber}: {step.name}
                            </span>
                            <span className="text-[#0E7044] font-mono">
                              {step.formula}
                            </span>
                          </div>
                          <p className="text-[#555555] text-[11px]">{step.description}</p>
                          <p className="text-[10px] text-[#767676] font-mono">
                            Rule: {step.ruleReference}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* PAYROLL WITHHOLDING VS ANNUAL LIABILITY NOTE */}
              <div className="bg-[#FFFBEB] rounded-xl border border-[#FDE68A] p-4 text-xs text-[#92400E] flex items-start gap-2.5">
                <AlertCircle className="w-4 h-4 text-[#D97706] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold mb-0.5">Payroll Withholding vs. Final Tax Liability</h4>
                  <p className="leading-relaxed">
                    {calculationResult.withholdingNote}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VIEW 6: MULTI-SCENARIO COMPARISON */}
        {activeTab === 'salary-comparison' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
              <h2 className="text-lg font-bold text-[#1A1A1A] mb-1 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#0E7044]" />
                Side-by-Side Salary & Tax Scenario Comparator
              </h2>
              <p className="text-xs text-[#767676] mb-6">
                Compare take-home pay, state tax disparities, and employer overhead across different states or salary levels (e.g. California vs Texas, or £60k vs £80k).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Scenario A Card */}
                <div className="p-5 bg-[#FAFAF8] rounded-xl border border-[#E5E5E0] space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-[#E5E5E0]">
                    <span className="text-xs font-bold text-[#0E7044] uppercase tracking-wider">Scenario A</span>
                    <span className="text-xs font-semibold text-[#555555]">{calculationResult.jurisdictionName}</span>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div>
                      <label className="block font-bold mb-1">Country</label>
                      <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="w-full bg-white border border-[#E5E5E0] rounded p-2 text-xs"
                      >
                        {Object.values(SALARY_COUNTRY_PACKS).map((p) => (
                          <option key={p.countryCode} value={p.countryCode}>
                            {p.countryName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {activeCountryPack.subdivisions && activeCountryPack.subdivisions.length > 0 && (
                      <div>
                        <label className="block font-bold mb-1">State / Province</label>
                        <select
                          value={selectedSubdivision}
                          onChange={(e) => setSelectedSubdivision(e.target.value)}
                          className="w-full bg-white border border-[#E5E5E0] rounded p-2 text-xs"
                        >
                          {activeCountryPack.subdivisions.map((s) => (
                            <option key={s.code} value={s.code}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block font-bold mb-1">Gross Annual Salary</label>
                      <input
                        type="number"
                        value={grossAmount}
                        onChange={(e) => setGrossAmount(Number(e.target.value))}
                        className="w-full bg-white border border-[#E5E5E0] rounded p-2 font-bold text-sm"
                      />
                    </div>
                  </div>

                  {/* Results for Scenario A */}
                  <div className="pt-4 border-t border-[#E5E5E0] space-y-2">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-[#767676]">Monthly Take-Home:</span>
                      <span className="text-xl font-extrabold text-[#0E7044]">
                        {calculationResult.monthlyNetPay.toLocaleString(undefined, { maximumFractionDigits: 0 })} {calculationResult.currency}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-[#555555]">
                      <span>Annual Net:</span>
                      <span className="font-bold">{calculationResult.annualNetPay.toLocaleString()} {calculationResult.currency}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#555555]">
                      <span>Effective Tax Rate:</span>
                      <span className="font-bold">{(calculationResult.effectiveTaxRate * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#555555]">
                      <span>Total Employer Cost:</span>
                      <span className="font-bold">{calculationResult.totalEmployerCost.toLocaleString()} {calculationResult.currency}</span>
                    </div>
                  </div>
                </div>

                {/* Scenario B Card */}
                <div className="p-5 bg-[#FAFAF8] rounded-xl border border-[#E5E5E0] space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-[#E5E5E0]">
                    <span className="text-xs font-bold text-[#2563EB] uppercase tracking-wider">Scenario B</span>
                    <span className="text-xs font-semibold text-[#555555]">{comparisonResultB.jurisdictionName}</span>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div>
                      <label className="block font-bold mb-1">Country</label>
                      <select
                        value={compareCountryB}
                        onChange={(e) => setCompareCountryB(e.target.value)}
                        className="w-full bg-white border border-[#E5E5E0] rounded p-2 text-xs"
                      >
                        {Object.values(SALARY_COUNTRY_PACKS).map((p) => (
                          <option key={p.countryCode} value={p.countryCode}>
                            {p.countryName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {SALARY_COUNTRY_PACKS[compareCountryB]?.subdivisions && (
                      <div>
                        <label className="block font-bold mb-1">State / Province</label>
                        <select
                          value={compareSubdivisionB}
                          onChange={(e) => setCompareSubdivisionB(e.target.value)}
                          className="w-full bg-white border border-[#E5E5E0] rounded p-2 text-xs"
                        >
                          {SALARY_COUNTRY_PACKS[compareCountryB].subdivisions.map((s) => (
                            <option key={s.code} value={s.code}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block font-bold mb-1">Gross Annual Salary</label>
                      <input
                        type="number"
                        value={compareGrossB}
                        onChange={(e) => setCompareGrossB(Number(e.target.value))}
                        className="w-full bg-white border border-[#E5E5E0] rounded p-2 font-bold text-sm"
                      />
                    </div>
                  </div>

                  {/* Results for Scenario B */}
                  <div className="pt-4 border-t border-[#E5E5E0] space-y-2">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-[#767676]">Monthly Take-Home:</span>
                      <span className="text-xl font-extrabold text-[#2563EB]">
                        {comparisonResultB.monthlyNetPay.toLocaleString(undefined, { maximumFractionDigits: 0 })} {comparisonResultB.currency}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-[#555555]">
                      <span>Annual Net:</span>
                      <span className="font-bold">{comparisonResultB.annualNetPay.toLocaleString()} {comparisonResultB.currency}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#555555]">
                      <span>Effective Tax Rate:</span>
                      <span className="font-bold">{(comparisonResultB.effectiveTaxRate * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#555555]">
                      <span>Total Employer Cost:</span>
                      <span className="font-bold">{comparisonResultB.totalEmployerCost.toLocaleString()} {comparisonResultB.currency}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Delta Summary Card */}
              <div className="mt-6 p-4 bg-[#E8F5E9] rounded-xl border border-[#C8E6C9] text-xs space-y-1">
                <h4 className="font-bold text-[#0E7044] text-sm">Comparison Outcome</h4>
                <p className="text-[#1A1A1A] leading-relaxed">
                  {calculationResult.monthlyNetPay > comparisonResultB.monthlyNetPay ? (
                    <>
                      <strong>Scenario A</strong> yields approximately{' '}
                      <strong>
                        {Math.abs(calculationResult.monthlyNetPay - comparisonResultB.monthlyNetPay).toLocaleString(
                          undefined,
                          { maximumFractionDigits: 0 }
                        )}{' '}
                        {calculationResult.currency} more net pay per month
                      </strong>{' '}
                      ({Math.abs(calculationResult.annualNetPay - comparisonResultB.annualNetPay).toLocaleString()} {calculationResult.currency}/year) compared to Scenario B.
                    </>
                  ) : calculationResult.monthlyNetPay < comparisonResultB.monthlyNetPay ? (
                    <>
                      <strong>Scenario B</strong> yields approximately{' '}
                      <strong>
                        {Math.abs(comparisonResultB.monthlyNetPay - calculationResult.monthlyNetPay).toLocaleString(
                          undefined,
                          { maximumFractionDigits: 0 }
                        )}{' '}
                        {comparisonResultB.currency} more net pay per month
                      </strong>{' '}
                      ({Math.abs(comparisonResultB.annualNetPay - calculationResult.annualNetPay).toLocaleString()} {comparisonResultB.currency}/year) compared to Scenario A.
                    </>
                  ) : (
                    'Both scenarios provide identical estimated net take-home compensation.'
                  )}
                </p>
                <p className="text-[11px] text-[#767676] italic mt-1">
                  Note: Comparison is strictly based on statutory income taxes and social security contributions. Local cost-of-living adjustments or housing indexes are not included in this payroll comparison.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* VIEW 7: COMMAND CENTER & QUALITY GATES */}
        {activeTab === 'command-center' && (
          <div className="space-y-6">
            {/* Telemetry Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-xl border border-[#E5E5E0] shadow-xs">
                <span className="text-xs text-[#767676] block">Active Tax Packs</span>
                <span className="text-2xl font-extrabold text-[#1A1A1A]">{SALARY_TELEMETRY_DATA.activeCountriesCount} Countries</span>
                <span className="text-[11px] text-[#0E7044] block mt-0.5">{SALARY_TELEMETRY_DATA.activeJurisdictionsCount} Subdivisions</span>
              </div>
              <div className="bg-white p-4 rounded-xl border border-[#E5E5E0] shadow-xs">
                <span className="text-xs text-[#767676] block">Golden Test Pass Rate</span>
                <span className="text-2xl font-extrabold text-[#0E7044]">{SALARY_TELEMETRY_DATA.goldenTestPassRate}%</span>
                <span className="text-[11px] text-[#767676] block mt-0.5">{goldenTestReport.totalTests} Regression Fixtures</span>
              </div>
              <div className="bg-white p-4 rounded-xl border border-[#E5E5E0] shadow-xs">
                <span className="text-xs text-[#767676] block">Avg Calculation Latency</span>
                <span className="text-2xl font-extrabold text-[#1A1A1A]">{SALARY_TELEMETRY_DATA.avgCalculationLatencyMs} ms</span>
                <span className="text-[11px] text-[#0E7044] block mt-0.5">Deterministic Runtime</span>
              </div>
              <div className="bg-white p-4 rounded-xl border border-[#E5E5E0] shadow-xs">
                <span className="text-xs text-[#767676] block">Open Tax Incidents</span>
                <span className="text-2xl font-extrabold text-[#0E7044]">{SALARY_TELEMETRY_DATA.taxDataIncidentsCount}</span>
                <span className="text-[11px] text-[#767676] block mt-0.5">Clean Governance State</span>
              </div>
            </div>

            {/* Golden Test Runner Table */}
            <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-bold text-[#1A1A1A] flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0E7044]" />
                    Golden Test Suite & Regression Verifier
                  </h3>
                  <p className="text-xs text-[#767676]">
                    Automated fixtures validating calculations against official government revenue tables
                  </p>
                </div>
                <span className="text-xs font-bold px-2.5 py-1 bg-[#E8F5E9] text-[#0E7044] rounded">
                  {goldenTestReport.passedCount} / {goldenTestReport.totalTests} Tests Passed (100%)
                </span>
              </div>

              <div className="border border-[#E5E5E0] rounded-lg overflow-hidden text-xs">
                <table className="w-full text-left">
                  <thead className="bg-[#FAFAF8] text-[#555555] font-bold border-b border-[#E5E5E0]">
                    <tr>
                      <th className="py-2.5 px-3">Test Scenario</th>
                      <th className="py-2.5 px-3">Jurisdiction</th>
                      <th className="py-2.5 px-3 text-right">Gross</th>
                      <th className="py-2.5 px-3 text-right">Expected Net</th>
                      <th className="py-2.5 px-3 text-right">Actual Net</th>
                      <th className="py-2.5 px-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0F0EC]">
                    {goldenTestReport.results.map((gt) => (
                      <tr key={gt.id} className="bg-white">
                        <td className="py-2 px-3 font-medium text-[#1A1A1A]">{gt.scenarioName}</td>
                        <td className="py-2 px-3 text-[#555555]">{gt.countryCode} {gt.subdivisionCode || ''}</td>
                        <td className="py-2 px-3 text-right font-medium">{gt.grossAnnual.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-[#555555]">{gt.expectedNetAnnual.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right font-bold text-[#0E7044]">{gt.actualNet.toLocaleString()}</td>
                        <td className="py-2 px-3 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E8F5E9] text-[#0E7044]">
                            <Check className="w-3 h-3" /> PASS
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Year-Over-Year Tax Rule Diffs */}
            <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-1 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-[#0E7044]" />
                Tax Ruleset Diff & Inflation Indexing Log (2025 vs 2026)
              </h3>
              <p className="text-xs text-[#767676] mb-4">
                Statutory threshold adjustments, standard deduction indexing, and wage ceiling expansions
              </p>

              <div className="space-y-4">
                {RECENT_TAX_DIFFS.map((diff, idx) => (
                  <div key={idx} className="p-4 bg-[#FAFAF8] rounded-lg border border-[#E5E5E0] text-xs">
                    <div className="flex items-center justify-between font-bold text-sm text-[#1A1A1A] mb-2">
                      <span>{diff.jurisdiction}</span>
                      <span className="text-xs text-[#0E7044]">
                        {diff.fromTaxYear} → {diff.toTaxYear}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {diff.changes.map((c, ci) => (
                        <div key={ci} className="bg-white p-2.5 rounded border border-[#E5E5E0]">
                          <span className="font-bold text-[#1A1A1A] block">{c.field}</span>
                          <span className="text-[11px] text-[#767676] block">
                            {c.fromValue} → <strong className="text-[#0E7044]">{c.toValue}</strong>
                          </span>
                          <span className="text-[10px] text-[#555555] italic">{c.impact}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* VIEW 8: ARCHITECTURE & ADRS */}
        {activeTab === 'architecture' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 shadow-xs">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2.5 py-0.5 rounded bg-[#E8F5E9] text-[#0E7044] text-xs font-bold">
                  PROMPT 06 DELIVERABLES
                </span>
                <span className="text-xs text-[#767676]">Architectural Decision Records & Specifications</span>
              </div>
              <h2 className="text-xl font-bold text-[#1A1A1A] mb-4">
                Global Salary, Paycheck & Compensation Architecture (DEL-P6-A to F)
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DELIVERABLES_P6.map((del) => (
                  <div key={del.code} className="p-4 bg-[#FAFAF8] rounded-xl border border-[#E5E5E0] text-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] font-bold text-[#0E7044] bg-[#E8F5E9] px-2 py-0.5 rounded">
                        {del.code}
                      </span>
                      <span className="text-[11px] text-[#767676] font-semibold">{del.category}</span>
                    </div>
                    <h3 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h3>
                    <p className="text-[#555555]">{del.summary}</p>
                    <ul className="list-disc pl-4 text-[11px] text-[#666666] space-y-1">
                      {del.details.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                    <p className="text-[10px] text-[#0E7044] font-medium pt-2 border-t border-[#E5E5E0]">
                      Architecture Note: {del.architectureNotes}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
